/**
 * Convex StorageDriver — Session 2 stub wiring for the 7 low-churn collections
 * (diets, diseases, foodTypes, locations, spellLibrary, npcLibrary,
 * globalSettings + currentLocation + journalSettings → `settings` singleton).
 *
 * This driver is CONSTRUCTED when Convex is configured (REACT_APP_CONVEX_URL set)
 * but isn't EXERCISED until Session 3 flips collectionRouting for the 7 paths.
 * Until then, the RoutedDriver's paths all route to Firebase and this driver's
 * methods never get called.
 *
 * Shape translation (the main value this driver adds beyond raw Convex calls):
 * the app's path-level shapes (keyed objects, arrays of strings) don't match
 * Convex's row-per-entry storage. Translation tables below handle the
 * bidirectional mapping so every consumer of useStorage sees the same shape
 * regardless of which backend holds the data.
 *
 * Uses `convex/browser` (not `convex/react`) because drivers sit outside React
 * hook boundaries — they need to subscribe + dispatch imperatively. Convex
 * function references are passed as strings ("diets:list") rather than imports
 * from `_generated/api` so this file compiles before `convex dev` first runs.
 */

import { ConvexClient } from 'convex/browser';
import {
  buildEmptyConnectedLootRuntime,
  buildEmptyConnectedShopRuntime,
  generateLocalId,
} from './StorageDriver.js';
import {
  buildProjectedViewerQueryArgs,
  normalizeProjectedViewerContext,
  shouldUseAuthedProjectedViewerQuery,
  shouldUseProjectedViewerQuery,
} from './convexViewerContext.js';

// --- Shape translation tables ---

// Convex list → app shape for a given collection path.
//
// Keyed libraries (diets/diseases/npcs) store rows as {<appId>, data: wholeValue}
// where `data` is the entire app-side object. We reassemble into the app's
// expected `{<appId>: wholeValue}` keyed shape by reading `row.data`.
//
// spells follows the same row shape but the app's shape is an ARRAY where each
// spell carries its own `id` field. We map spellId back onto `id` during read
// so consumers see the array shape unchanged.
const ROWS_TO_APP_SHAPE = {
  diets: (rows) => Object.fromEntries(rows.map(r => [r.dietId, r.data])),
  diseases: (rows) => Object.fromEntries(rows.map(r => [r.diseaseId, r.data])),
  npcLibrary: (rows) => Object.fromEntries(rows.map(r => [r.npcId, r.data])),
  spellLibrary: (rows) => rows.map(r => ({ ...(r.data || {}), id: r.spellId })),
  // Session 4: chars + systems store the blob as a JSON-encoded string
  // (convex/schema.ts explains why — non-ASCII object-key constraint). Parse
  // on read; fall back to empty object if the stored string is malformed so
  // a single bad row doesn't crash the whole list subscription.
  chars: (rows) => Object.fromEntries(rows.map(r => {
    const parsed = safeParseJson(r.data, {});
    const campaignCharacterKind = r.campaignCharacterKind === 'test' || r.isTestCharacter === true || typeof r.convertedFromTestAt === 'number'
      ? 'test'
      : 'player';
    return [r.charId, {
      ...parsed,
      id: r.charId,
      charId: r.charId,
      campaignCharacterKind,
      isTestCharacter: campaignCharacterKind === 'test',
      testPresetKey: campaignCharacterKind === 'test' && typeof r.testPresetKey === 'string'
        ? r.testPresetKey
        : null,
      testPresetCategories: campaignCharacterKind === 'test' && r.testPresetCategories && typeof r.testPresetCategories === 'object'
        ? r.testPresetCategories
        : null,
      isPromotedTestCharacter: campaignCharacterKind === 'test' && (
        typeof r.testPromotedAt === 'number'
        || typeof r.convertedFromTestAt === 'number'
      ),
    }];
  })),
  systems: (rows) => Object.fromEntries(rows.map(r => [r.systemId, safeParseJson(r.data, {})])),
  foodTypes: (rows) => rows.map(r => r.name),
  locations: (rows) => rows.map(r => r.name),
  // settings slice queries return the bare value — direct passthrough below.
};

// Session A (2026-04-28) + Session B (2026-04-29): per-record write paths.
//
// For collections that store one row per app-id (chars, spells, npcs), the
// driver diffs the saved app-shape map against `lastDispatched[path]` and
// emits one `patchOne` / `deleteOne` mutation per changed/removed record.
// This replaces the whole-collection `upsertAll` write with surgical patches —
// at typical usage, ~95% bandwidth reduction.
//
// `extractMap` normalizes the app-side shape (which differs per path —
// chars/npcLibrary are keyed maps, spellLibrary is an array of objects with
// .id) into a uniform `{key: value}` shape for diffing. Reference equality
// across the resulting map is preserved when callers use the React update
// patterns ({...prev, [k]: x} / arr.map(x => x.id === id ? {...x, ...e} : x))
// — unchanged entries keep their refs, so the diff dispatches only changed.
//
// `encode` mirrors the schema column type:
//   v.string() (chars/systems) — JSON.stringify
//   v.any()    (spells/npcs)  — passthrough
const stripRuntimeCharacterMetadata = (value = {}) => {
  const {
    campaignCharacterKind,
    isTestCharacter,
    testPresetKey,
    testPresetCategories,
    isPromotedTestCharacter,
    ...rest
  } = value || {};
  void campaignCharacterKind;
  void isTestCharacter;
  void testPresetKey;
  void testPresetCategories;
  void isPromotedTestCharacter;
  return rest;
};

const PER_RECORD_PATHS = {
  chars: {
    keyField: 'charId',
    extractMap: (value) => value || {},
    encode: (val) => JSON.stringify(stripRuntimeCharacterMetadata(val ?? {})),
    patchMutation: 'chars:patchOne',
    deleteMutation: 'chars:deleteOne',
  },
  systems: {
    keyField: 'systemId',
    extractMap: (value) => value || {},
    encode: (val) => JSON.stringify(val ?? {}),
    patchMutation: 'systems:patchOne',
    deleteMutation: 'systems:deleteOne',
    deleteRequiresFullSnapshot: true,
  },
  npcLibrary: {
    keyField: 'npcId',
    extractMap: (value) => value || {},
    encode: (val) => val,
    patchMutation: 'npcs:patchOne',
    deleteMutation: 'npcs:deleteOne',
  },
  spellLibrary: {
    keyField: 'spellId',
    extractMap: (value) => {
      const arr = Array.isArray(value) ? value : [];
      const map = {};
      for (const s of arr) {
        if (s && s.id) map[s.id] = s;
      }
      return map;
    },
    encode: (val) => val,
    patchMutation: 'spells:patchOne',
    deleteMutation: 'spells:deleteOne',
  },
};

// Session E (2026-04-29): messages per-row diff dispatch.
//
// Messages have a structurally different shape than chars/spells/npcs —
// {statusUpdates: [...], dmChats: {[charId]: [...]}} instead of a flat
// keyed map. So they get their own diff handler rather than fitting into
// PER_RECORD_PATHS.
//
// Flatten the blob into a flat {messageId: {channel, message}} map. Diff
// against lastDispatched on message ref equality (the wrapper is rebuilt
// each call but the underlying message ref survives via React's update
// patterns). Dispatch upsertOne for new/changed, deleteOne for removed.
//
// Because the wrapper objects are new each call, we compare the inner
// `message` ref directly. The channel comes along for routing.
const flattenMessagesBlob = (value) => {
  const map = {};
  for (const m of (value?.statusUpdates || [])) {
    if (m && m.id) map[m.id] = { channel: 'status', message: m };
  }
  for (const [charId, msgs] of Object.entries(value?.dmChats || {})) {
    for (const m of (msgs || [])) {
      if (m && m.id) map[m.id] = { channel: `dm-chat:${charId}`, message: m };
    }
  }
  return map;
};

// App shape → Convex mutation args for save().
//
// For the keyed libraries, pack the whole value into `data` and surface only
// the app id at the top level. This matches the Convex schema (§17 / schema.ts).
const JOURNAL_ROW_MUTATIONS = {
  journal: {
    upsertMutation: 'journal:upsertEntry',
    deleteMutation: 'journal:deleteEntry',
  },
  dmJournal: {
    upsertMutation: 'dmJournal:upsertEntry',
    deleteMutation: 'dmJournal:deleteEntry',
  },
};

const flattenJournalEntries = (value) => {
  const entries = Array.isArray(value) ? value.filter(Boolean) : [];
  const map = {};
  let missingIds = 0;
  let duplicateIds = 0;
  for (const entry of entries) {
    const id = typeof entry?.id === 'string' ? entry.id.trim() : '';
    if (!id) {
      missingIds += 1;
      continue;
    }
    if (map[id]) {
      duplicateIds += 1;
      continue;
    }
    map[id] = entry;
  }
  return { map, missingIds, duplicateIds };
};

const normalizeEntryCommandPayload = (args = {}, label = 'entry') => {
  const entry = args.entry && typeof args.entry === 'object' ? args.entry : args;
  const entryId = typeof args.entryId === 'string' && args.entryId.trim()
    ? args.entryId.trim()
    : (typeof entry?.id === 'string' ? entry.id.trim() : '');
  if (!entryId) throw new Error(`${label} id is required`);
  return {
    entryId,
    entry: { ...(entry || {}), id: entryId },
  };
};

const normalizeEntryDeletePayload = (args = {}, label = 'entry') => {
  const entryId = typeof args.entryId === 'string' && args.entryId.trim()
    ? args.entryId.trim()
    : (typeof args.id === 'string' ? args.id.trim() : '');
  if (!entryId) throw new Error(`${label} id is required`);
  return { entryId };
};

const normalizeJournalNameLookupPayload = (args = {}) => {
  const sourceLookups = Array.isArray(args.lookups) && args.lookups.length > 0
    ? args.lookups
    : [{ category: args.category, name: args.name, nameKey: args.nameKey }];
  const lookups = sourceLookups
    .map(lookup => ({
      category: typeof lookup?.category === 'string' ? lookup.category.trim() : '',
      ...(typeof lookup?.name === 'string' ? { name: lookup.name } : {}),
      ...(typeof lookup?.nameKey === 'string' ? { nameKey: lookup.nameKey } : {}),
    }))
    .filter(lookup => lookup.category && (lookup.name || lookup.nameKey));
  return {
    scopeKey: args.scopeKey ?? null,
    lookups,
    ...(Number.isFinite(args.limit) ? { limit: Math.trunc(args.limit) } : {}),
  };
};

const normalizeStringList = (value, maxItems = 20, maxLength = 120) => (
  Array.isArray(value)
    ? value
        .filter(item => typeof item === 'string' && item.trim())
        .map(item => item.trim().slice(0, maxLength))
        .slice(0, maxItems)
    : undefined
);

const normalizeJournalDiscoveryPayload = (args = {}) => {
  const payload = {
    scopeKey: args.scopeKey ?? null,
  };
  if (typeof args.query === 'string') payload.query = args.query.trim().slice(0, 120);
  const categories = normalizeStringList(args.categories, 12, 48);
  if (categories) payload.categories = categories;
  const npcRefs = normalizeStringList(args.npcRefs, 20, 120);
  if (npcRefs) payload.npcRefs = npcRefs;
  const questNpcNames = normalizeStringList(args.questNpcNames, 20, 120);
  if (questNpcNames) payload.questNpcNames = questNpcNames;
  if (typeof args.includeSessionMeta === 'boolean') payload.includeSessionMeta = args.includeSessionMeta;
  ['limit', 'categoryLimit', 'sessionLimit', 'questLimit'].forEach((key) => {
    if (Number.isFinite(args[key])) payload[key] = Math.trunc(args[key]);
  });
  return payload;
};

const APP_SHAPE_TO_MUTATION_ARGS = {
  diets: (value) => ({
    values: Object.entries(value || {}).map(([dietId, d]) => ({ dietId, data: d })),
  }),
  diseases: (value) => ({
    values: Object.entries(value || {}).map(([diseaseId, d]) => ({ diseaseId, data: d })),
  }),
  npcLibrary: (value) => ({
    values: Object.entries(value || {}).map(([npcId, n]) => ({ npcId, data: n })),
  }),
  spellLibrary: (value) => ({
    values: (value || []).map(s => ({ spellId: s.id, data: s })),
  }),
  // Session 4: stringify on write (see ROWS_TO_APP_SHAPE for rationale).
  chars: (value) => ({
    values: Object.entries(value || {}).map(([charId, c]) => ({ charId, data: JSON.stringify(c ?? {}) })),
  }),
  systems: (value) => ({
    values: Object.entries(value || {}).map(([systemId, s]) => ({ systemId, data: JSON.stringify(s ?? {}) })),
  }),
  foodTypes: (value) => ({ names: Array.isArray(value) ? value : [] }),
  locations: (value) => ({ names: Array.isArray(value) ? value : [] }),
};

// Convex function name per collection path. String-based so we don't depend on
// `_generated/api.js` existing at build time.
const QUERY_FUNCTIONS = {
  diets: 'diets:list',
  diseases: 'diseases:list',
  npcLibrary: 'npcs:list',
  spellLibrary: 'spells:list',
  chars: 'chars:list',
  systems: 'systems:list',
  foodTypes: 'foodTypes:list',
  locations: 'locations:list',
  globalSettings: 'settings:getGlobalSettings',
  currentLocation: 'settings:getCurrentLocation',
  journalSettings: 'settings:getJournalSettings',
  // Session 5 singleton blobs — each returns the single row (or null).
  combat: 'combat:get',
  combatSnapshots: 'combatSnapshots:get',
  journal: 'journal:get',
  dmJournal: 'dmJournal:get',
  audit: 'audit:get',
  auditSessionMeta: 'audit:getSessionMeta',
  auditMeta: 'audit:getMeta',
  auditRecent: 'audit:listRecent',
  // Session 6 singleton blobs — last 3 paths.
  messages: 'messages:get',
  messageLiveTail: 'messages:getLiveTail',
  messagePanelHistory: 'messages:getPanelHistory',
  messageUnreadCounts: 'messages:getUnreadCounts',
  groupTravelLog: 'groupTravelLog:get',
  food: 'food:get',
  journalRecent: 'journal:listRecent',
  journalPage: 'journal:listPage',
  journalEntry: 'journal:getEntry',
  dmJournalRecent: 'dmJournal:listRecent',
  dmJournalPage: 'dmJournal:listPage',
  dmJournalEntry: 'dmJournal:getEntry',
  // 2026-04-23: party shared inventory.
  partyInventory: 'partyInventory:get',
  // 2026-05-05: canonical item-domain shop runtime projection.
  shopRuntime: 'shops:listForAuthedViewer',
  // 2026-05-05: canonical item-domain loot runtime projection.
  lootRuntime: 'lootRuntime:listForAuthedViewer',
};

const SETTINGS_SUBSCRIPTION_KEY = '__settings:getAll';
const SETTINGS_SUBSCRIPTION_PATHS = new Set(['globalSettings', 'currentLocation', 'journalSettings']);
const SETTINGS_FIELD_BY_PATH = {
  globalSettings: 'globalSettings',
  currentLocation: 'currentLocation',
  journalSettings: 'journalSettings',
};

// Session 7B: viewer-aware variants. When the driver is constructed with a
// non-null `viewerContext`, `chars` and `systems` subscriptions route here
// instead of the defaults above. The server-side projection strips DM-only
// fields (hiddenEffects, automationRules, dmNotes) for player viewers and
// passes DM viewers through unchanged.
//
// Only `chars` + `systems` have projected variants today. Other paths (diets,
// combat, journal, etc.) don't carry three-axis privacy per CLAUDE.md §3 P3;
// if they gain it later, add entries here and in the server query exports.
const PROJECTED_QUERY_FUNCTIONS = {
  chars: 'chars:listForViewer',
  systems: 'systems:listForViewer',
};

const AUTHED_PROJECTED_QUERY_FUNCTIONS = {
  chars: 'chars:listForAuthedViewer',
  systems: 'systems:listForAuthedViewer',
};

const MUTATION_FUNCTIONS = {
  diets: 'diets:upsertAll',
  diseases: 'diseases:upsertAll',
  npcLibrary: 'npcs:upsertAll',
  spellLibrary: 'spells:upsertAll',
  chars: 'chars:upsertAll',
  systems: 'systems:upsertAll',
  foodTypes: 'foodTypes:upsertAll',
  locations: 'locations:upsertAll',
  globalSettings: 'settings:setGlobalSettings',
  currentLocation: 'settings:setCurrentLocation',
  journalSettings: 'settings:setJournalSettings',
  // Session 5 — whole-singleton replace via the `set` mutation per table.
  combat: 'combat:set',
  combatSnapshots: 'combatSnapshots:set',
  journal: 'journal:set',
  dmJournal: 'dmJournal:set',
  audit: 'audit:set',
  // Session 6.
  messages: 'messages:set',
  groupTravelLog: 'groupTravelLog:set',
  food: 'food:set',
  // Party inventory intentionally has no whole-blob Convex writer. Connected
  // routes must use item-specific authority mutations instead.
};

// Singleton blob paths — subscription result is `row | null` (not an array),
// and the row's `data` field is a JSON-encoded string. Read: parse; write:
// stringify. Defaults below are the fallback shape when the row doesn't yet
// exist (pre-seed state) — matches the normalizer defaults in StorageDriver.js.
const SINGLETON_BLOB_PATHS = new Set([
  'combat', 'combatSnapshots', 'journal', 'dmJournal', 'audit',
  // Session 6 additions:
  'messages', 'groupTravelLog', 'food',
  // 2026-04-23: party shared inventory.
  'partyInventory',
]);

const SINGLETON_BLOB_DEFAULTS = {
  combat: { active: false, combatants: [], enemies: [], round: 1, turnIndex: 0, damageLog: [] },
  combatSnapshots: [],
  journal: [],
  dmJournal: [],
  audit: { activeSessionId: null, sessions: {}, events: {}, snapshots: {} },
  messages: { statusUpdates: [], dmChats: {} },
  groupTravelLog: [],
  food: { totalUnits: 0, foodLog: [] },
  partyInventory: { items: [] },
};

const VIRTUAL_SUBSCRIPTION_DEFAULTS = {
  auditSessionMeta: { activeSessionId: null, sessions: {} },
  auditMeta: { activeSessionId: null, sessions: {}, snapshots: {} },
  auditRecent: { events: {} },
  messageLiveTail: { statusUpdates: [], dmChats: {} },
  messagePanelHistory: { statusUpdates: [], dmChats: {} },
  messageUnreadCounts: { statusUnreadCount: 0, dmUnreadCount: 0 },
  journalRecent: [],
  journalPage: { page: [], isDone: true, continueCursor: null },
  journalEntry: null,
  dmJournalRecent: [],
  dmJournalPage: { page: [], isDone: true, continueCursor: null },
  dmJournalEntry: null,
};

// JSON.parse wrapper that never throws — used for the string-encoded data
// fields on chars + systems. Malformed strings fall back to the provided
// default rather than crashing the whole subscription. Not a bare try/catch
// on every call site so the intent stays visible.
const safeParseJson = (str, fallback) => {
  if (typeof str !== 'string' || !str) return fallback;
  try { return JSON.parse(str); }
  catch (e) {
    console.error('ConvexDriver: JSON.parse failed on stored blob', e, str?.slice(0, 80));
    return fallback;
  }
};

const materializeJournalEntries = (rows) => (
  Array.isArray(rows)
    ? rows.map(row => row?.entry ?? row).filter(Boolean)
    : []
);

const normalizeJournalPagePayload = (raw) => ({
  page: materializeJournalEntries(raw?.page),
  isDone: raw?.isDone !== false,
  continueCursor: raw?.continueCursor ?? null,
});

// Normalize a raw Convex query result for a given app path to the shape the
// app expects. Handles null (collection not yet created).
const normalizeForPath = (path, raw) => {
  if (path === 'shopRuntime') return raw || buildEmptyConnectedShopRuntime();
  if (path === 'lootRuntime') return raw || buildEmptyConnectedLootRuntime();

  if (path in VIRTUAL_SUBSCRIPTION_DEFAULTS) {
    if (path === 'journalRecent' || path === 'dmJournalRecent') {
      return materializeJournalEntries(raw?.entries);
    }
    if (path === 'journalPage' || path === 'dmJournalPage') {
      return normalizeJournalPagePayload(raw);
    }
    if (path === 'journalEntry' || path === 'dmJournalEntry') {
      return raw?.entry ?? null;
    }
    return raw || VIRTUAL_SUBSCRIPTION_DEFAULTS[path];
  }

  // Session 5 singleton blobs: raw is `row | null`, row.data is JSON-encoded.
  // Validate the parsed value's SHAPE matches the default's shape (array vs
  // object). A bad previous write can leave `data` as a valid JSON string like
  // `'"session-id"'` that parses without error but isn't the expected shape.
  // Without the shape check, a bare string leaks into `audit.activeSessionId`
  // lookups and the UI shows "Active LIVE" + "Invalid Date" because the
  // session can't be found. Fall back to default so the app sees a consistent
  // empty state until the next write repairs the stored blob.
  if (SINGLETON_BLOB_PATHS.has(path)) {
    if (!raw) return SINGLETON_BLOB_DEFAULTS[path];
    const parsed = safeParseJson(raw.data, SINGLETON_BLOB_DEFAULTS[path]);
    const defaultShape = SINGLETON_BLOB_DEFAULTS[path];
    const defaultIsArray = Array.isArray(defaultShape);
    if (defaultIsArray) {
      if (!Array.isArray(parsed)) {
        console.warn(`ConvexDriver: ${path} blob isn't an array, using default. Stored value:`, raw.data?.slice(0, 80));
        return defaultShape;
      }
    } else {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        console.warn(`ConvexDriver: ${path} blob isn't a plain object, using default. Stored value:`, raw.data?.slice(0, 80));
        return defaultShape;
      }
    }
    return parsed;
  }
  if (path in ROWS_TO_APP_SHAPE) {
    if (!Array.isArray(raw)) return path === 'spellLibrary' || path === 'foodTypes' || path === 'locations' ? [] : {};
    return ROWS_TO_APP_SHAPE[path](raw);
  }
  // Settings slice queries return the bare value.
  return raw;
};

/**
 * Session 7B: route chars/systems subscriptions through projected queries
 * when a viewerContext is supplied. Args passed to the Convex query also
 * depend on the path — see pickQueryArgs.
 */
const pickQueryFunction = (path, viewerContext, useServerDerivedViewerQueries) => {
  if (shouldUseAuthedProjectedViewerQuery(path, viewerContext, useServerDerivedViewerQueries)) {
    return AUTHED_PROJECTED_QUERY_FUNCTIONS[path];
  }
  if (shouldUseProjectedViewerQuery(path, viewerContext)) {
    return PROJECTED_QUERY_FUNCTIONS[path];
  }
  return QUERY_FUNCTIONS[path];
};

const viewerCharIdFromContext = (viewerContext) => (
  viewerContext?.viewerKind === 'player' && viewerContext?.viewerCharId
    ? viewerContext.viewerCharId
    : null
);

const asPositiveInteger = (value, fallback, max = 500) => {
  const n = Number.isFinite(value) ? Math.trunc(value) : fallback;
  return Math.max(1, Math.min(max, n));
};

const defaultSubscriptionArgsForPath = (path, viewerContext) => {
  const charId = viewerCharIdFromContext(viewerContext);
  if (path === 'auditRecent') return { limit: 100 };
  if (path === 'messageLiveTail') return { charId, statusLimit: 50, dmLimit: 50 };
  if (path === 'messagePanelHistory') return { charId, statusLimit: 200, dmLimit: 200 };
  if (path === 'messageUnreadCounts') return { charId, statusLimit: 100, dmLimit: 100 };
  if (path === 'journalRecent' || path === 'dmJournalRecent') {
    return { scopeKey: null, category: null, limit: 50 };
  }
  if (path === 'journalPage' || path === 'dmJournalPage') {
    return {
      scopeKey: null,
      category: null,
      sessionId: null,
      paginationOpts: { cursor: null, numItems: 200 },
    };
  }
  if (path === 'journalEntry' || path === 'dmJournalEntry') {
    return { scopeKey: null, entryId: '' };
  }
  return {};
};

const mergeSubscriptionArgs = (path, viewerContext, overrides) => {
  const base = defaultSubscriptionArgsForPath(path, viewerContext);
  const next = { ...base, ...(overrides && typeof overrides === 'object' ? overrides : {}) };
  if (path === 'messageLiveTail' || path === 'messagePanelHistory' || path === 'messageUnreadCounts') {
    return {
      charId: next.charId ?? null,
      statusLimit: asPositiveInteger(next.statusLimit, base.statusLimit),
      dmLimit: asPositiveInteger(next.dmLimit, base.dmLimit),
    };
  }
  if (path === 'auditRecent') {
    return { limit: asPositiveInteger(next.limit, 100, 500) };
  }
  if (path === 'journalRecent' || path === 'dmJournalRecent') {
    return {
      scopeKey: next.scopeKey ?? null,
      category: next.category ?? null,
      limit: asPositiveInteger(next.limit, 50, 100),
    };
  }
  if (path === 'journalPage' || path === 'dmJournalPage') {
    const paginationOpts = {
      ...(base.paginationOpts || {}),
      ...(next.paginationOpts || {}),
    };
    if (next.cursor !== undefined) paginationOpts.cursor = next.cursor;
    if (next.numItems !== undefined) paginationOpts.numItems = next.numItems;
    return {
      scopeKey: next.scopeKey ?? null,
      category: next.category ?? null,
      sessionId: next.sessionId ?? null,
      paginationOpts: {
        cursor: paginationOpts.cursor ?? null,
        numItems: asPositiveInteger(paginationOpts.numItems, 200, 500),
      },
    };
  }
  if (path === 'journalEntry' || path === 'dmJournalEntry') {
    return { scopeKey: next.scopeKey ?? null, entryId: next.entryId ?? '' };
  }
  return next;
};

const stableArgsKey = (args) => {
  try { return JSON.stringify(args ?? null); }
  catch { return String(args); }
};

const pickQueryArgs = (path, viewerContext, useServerDerivedViewerQueries, overrideArgs = null) => {
  if (shouldUseAuthedProjectedViewerQuery(path, viewerContext, useServerDerivedViewerQueries)) {
    return {};
  }
  if (path in VIRTUAL_SUBSCRIPTION_DEFAULTS) {
    return mergeSubscriptionArgs(path, viewerContext, overrideArgs);
  }
  return buildProjectedViewerQueryArgs(path, viewerContext);
};

/**
 * @param {{
 *   viewerContext?: { viewerKind: 'dm'|'player', viewerCharId?: string|null } | null,
 *   viewerAuthToken?: string | null,
 *   useServerDerivedViewerQueries?: boolean,
 * }} [options]
 * @returns {import('./StorageDriver.js').StorageDriver | null}
 *   Returns null if REACT_APP_CONVEX_URL is not set (Convex not configured yet).
 *
 * Session 7B: when `viewerContext` is passed, chars + systems subscriptions
 * route through projected queries (`chars:listForViewer`,
 * `systems:listForViewer`) with the viewer args. Other paths unchanged.
 * If viewerContext is null/undefined, behavior matches Session 1-6 exactly.
 */
export const createConvexDriver = (options = {}) => {
  const {
    viewerContext = null,
    viewerAuthToken = null,
    useServerDerivedViewerQueries = false,
  } = options;
  const normalizedViewerContext = normalizeProjectedViewerContext(viewerContext);
  // Read URL from CRA's env substitution. Convex dev sets this in .env.local
  // when the deployment is created. Without it, we can't construct a client.
  const url = process.env.REACT_APP_CONVEX_URL;
  if (!url) {
    console.log('Convex not configured (REACT_APP_CONVEX_URL unset). Run `npx convex dev` to create a deployment.');
    return null;
  }

  if (viewerContext && !normalizedViewerContext) {
    console.warn(
      'ConvexDriver: invalid viewerContext supplied, falling back to raw chars/systems queries.',
      viewerContext
    );
  }

  const client = new ConvexClient(url);
  let currentViewerAuthToken = viewerAuthToken;
  const applyViewerAuthToken = () => {
    client.setAuth(async () => currentViewerAuthToken || null);
  };
  if (viewerAuthToken !== null) {
    applyViewerAuthToken();
  }

  /** @type {Map<string, Set<(value: any) => void>>} */
  const handlers = new Map();
  /** @type {Map<string, any>} */
  const cache = new Map();
  /** @type {Map<string, () => void>} */
  const subscriptions = new Map();
  /** @type {Map<string, any>} */
  const subscriptionArgsByPath = new Map();
  /** @type {Map<string, string>} */
  const subscriptionArgKeysByPath = new Map();
  // Session A (2026-04-28): per-record paths track "what's last in the
  // database from this client's perspective" — updated on save dispatch AND
  // subscription delivery. Used to compute diffs in save(). Not the same
  // as `cache` because cache is the current-value mirror; lastDispatched
  // is the "what we and the server both think is true" reference for diff.
  // (In steady state they're the same; the distinction only matters in
  // mid-flight save windows.)
  /** @type {Map<string, any>} */
  const lastDispatched = new Map();
  const trustedFullSnapshots = new Set();

  const getHandlers = (path) => {
    let set = handlers.get(path);
    if (!set) { set = new Set(); handlers.set(path, set); }
    return set;
  };

  const notify = (path) => {
    const hs = handlers.get(path);
    if (!hs || hs.size === 0) return;
    const value = cache.get(path);
    hs.forEach(h => { try { h(value); } catch (e) { console.error(`ConvexDriver handler error on ${path}:`, e); } });
  };

  let connectionStatus = /** @type {import('./StorageDriver.js').ConnectionStatus} */ ('checking');
  // Mark connected after the first successful query delivery OR mutation. Both
  // count as evidence the connection is alive. Previously this only triggered
  // from mutations, which wedged the loading screen in session-6 mode (all
  // paths routed to Convex with no early write to kick it — Firebase's
  // 'connected' signal no longer compensated).
  const markConnectedOnSuccess = (p) => p.then(v => { connectionStatus = 'connected'; return v; });

  const hasHandlers = (path) => {
    const set = handlers.get(path);
    return !!set && set.size > 0;
  };

  const emitDefaultForPath = (path) => {
    if (!(path in VIRTUAL_SUBSCRIPTION_DEFAULTS)) return;
    cache.set(path, VIRTUAL_SUBSCRIPTION_DEFAULTS[path]);
    notify(path);
  };

  const teardownSubscription = (key, label = key) => {
    const unsubscribe = subscriptions.get(key);
    if (unsubscribe) {
      try { unsubscribe(); } catch (e) { console.error(`ConvexDriver unsubscribe error on ${label}:`, e); }
    }
    subscriptions.delete(key);
  };

  const anySettingsHandlers = () => (
    [...SETTINGS_SUBSCRIPTION_PATHS].some(path => hasHandlers(path))
  );

  const ensureSettingsSubscription = () => {
    if (subscriptions.has(SETTINGS_SUBSCRIPTION_KEY)) return;
    const unsub = client.onUpdate('settings:getAll', {}, (raw) => {
      if (connectionStatus === 'checking') connectionStatus = 'connected';
      for (const path of SETTINGS_SUBSCRIPTION_PATHS) {
        const field = SETTINGS_FIELD_BY_PATH[path];
        const normalized = normalizeForPath(path, raw?.[field] ?? null);
        cache.set(path, normalized);
        notify(path);
      }
    });
    subscriptions.set(SETTINGS_SUBSCRIPTION_KEY, unsub);
  };

  const ensureSubscription = (path) => {
    if (SETTINGS_SUBSCRIPTION_PATHS.has(path)) {
      ensureSettingsSubscription();
      return;
    }
    if (subscriptions.has(path)) return;
    const queryFn = pickQueryFunction(
      path,
      normalizedViewerContext,
      useServerDerivedViewerQueries,
    );
    if (!queryFn) return; // path not handled by Convex in this session
    const queryArgs = pickQueryArgs(
      path,
      normalizedViewerContext,
      useServerDerivedViewerQueries,
      subscriptionArgsByPath.get(path),
    );
    // onUpdate returns an unsubscribe function. First-value callback fires
    // once Convex delivers the initial result.
    const unsub = client.onUpdate(queryFn, queryArgs, (raw) => {
      // First-value delivery → we're reachable. Promotes loading indicator.
      if (connectionStatus === 'checking') connectionStatus = 'connected';
      const normalized = normalizeForPath(path, raw);
      cache.set(path, normalized);
      // Session A/B: update lastDispatched on subscription delivery so
      // per-record diffs against the latest known server state, not against
      // stale local intent. Without this, foreign edits (another client
      // added a char/spell/npc) would trigger redundant patchOne dispatches
      // on our next save.
      //
      // Run normalized through extractMap so lastDispatched holds the same
      // shape that save()'s diff loop iterates. For chars/npcLibrary the
      // normalized value IS already a keyed map (extractMap is identity).
      // For spellLibrary the normalized value is an array, and extractMap
      // converts to {spellId: spell}.
      if (PER_RECORD_PATHS[path]) {
        lastDispatched.set(path, PER_RECORD_PATHS[path].extractMap(normalized));
      }
      // Session E: messages have their own flatten function (structurally
      // different from PER_RECORD_PATHS).
      if (path === 'messages') {
        lastDispatched.set('messages', flattenMessagesBlob(normalized));
        trustedFullSnapshots.add('messages');
      }
      if (path === 'journal' || path === 'dmJournal') {
        lastDispatched.set(path, flattenJournalEntries(normalized).map);
        trustedFullSnapshots.add(path);
      }
      notify(path);
    });
    subscriptions.set(path, unsub);
  };

  return {
    kind: 'convex',

    setViewerAuthToken(nextToken) {
      currentViewerAuthToken = nextToken ?? null;
      applyViewerAuthToken();
    },

    setSubscriptionArgs(path, args) {
      const nextArgs = args && typeof args === 'object' ? args : null;
      const nextKey = stableArgsKey(nextArgs);
      if (subscriptionArgKeysByPath.get(path) === nextKey) return;
      if (nextArgs) subscriptionArgsByPath.set(path, nextArgs);
      else subscriptionArgsByPath.delete(path);
      subscriptionArgKeysByPath.set(path, nextKey);
      if (!hasHandlers(path) || SETTINGS_SUBSCRIPTION_PATHS.has(path)) return;
      teardownSubscription(path);
      cache.delete(path);
      lastDispatched.delete(path);
      trustedFullSnapshots.delete(path);
      emitDefaultForPath(path);
      ensureSubscription(path);
    },

    getSubscriptionDebugSnapshot() {
      return {
        handlerCountsByPath: Object.fromEntries(
          [...handlers.entries()].map(([path, set]) => [path, set.size])
        ),
        activeSubscriptionKeys: [...subscriptions.keys()],
      };
    },

    subscribe(path, handler) {
      // Session 7B: pickQueryFunction uses the projected variant for chars +
      // systems when viewerContext is set. Fall back to the default map for
      // the existence check — both sets share the same paths.
      if (!(path in QUERY_FUNCTIONS)) {
        // Path not handled by Convex in this session — this means the routing
        // map is misconfigured (a path routed to 'convex' but no driver
        // support). Log and emit nothing; RoutedDriver will have already
        // noticed the mismatch.
        console.warn(`ConvexDriver has no query function for path "${path}". Check collectionRouting.`);
        return () => {};
      }
      const set = getHandlers(path);
      set.add(handler);
      ensureSubscription(path);
      // If we already have a cached value, emit it synchronously.
      if (cache.has(path)) {
        try { handler(cache.get(path)); } catch (e) { console.error(e); }
      }
      return () => {
        set.delete(handler);
        if (set.size > 0) return;
        handlers.delete(path);
        if (SETTINGS_SUBSCRIPTION_PATHS.has(path)) {
          cache.delete(path);
          if (!anySettingsHandlers()) {
            teardownSubscription(SETTINGS_SUBSCRIPTION_KEY, 'settings:getAll');
            SETTINGS_SUBSCRIPTION_PATHS.forEach(p => cache.delete(p));
          }
          return;
        }
        teardownSubscription(path);
        cache.delete(path);
        lastDispatched.delete(path);
        trustedFullSnapshots.delete(path);
      };
    },

    async markStatusMessageSeen(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('messages:markStatusSeen', {
          messageId: args.messageId,
        }).catch(e => {
          console.error('ConvexDriver.markStatusMessageSeen failed:', e);
          throw e;
        })
      );
    },

    async dismissStatusMessage(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('messages:dismissStatus', {
          messageId: args.messageId,
        }).catch(e => {
          console.error('ConvexDriver.dismissStatusMessage failed:', e);
          throw e;
        })
      );
    },

    async markDmChatRead(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('messages:markDmChatRead', {
          charId: args.charId,
          messageIds: Array.isArray(args.messageIds) ? args.messageIds : [],
        }).catch(e => {
          console.error('ConvexDriver.markDmChatRead failed:', e);
          throw e;
        })
      );
    },

    async sendStatusMessage(args = {}) {
      const id = args.message?.id || generateLocalId();
      const message = { ...(args.message || {}), id };
      return await markConnectedOnSuccess(
        client.mutation('messages:sendStatus', {
          messageId: id,
          data: JSON.stringify(message),
        }).catch(e => {
          console.error('ConvexDriver.sendStatusMessage failed:', e);
          throw e;
        })
      );
    },

    async sendDmChatMessage(args = {}) {
      const id = args.message?.id || generateLocalId();
      const message = { ...(args.message || {}), id };
      return await markConnectedOnSuccess(
        client.mutation('messages:sendDmChat', {
          charId: args.charId,
          messageId: id,
          data: JSON.stringify(message),
        }).catch(e => {
          console.error('ConvexDriver.sendDmChatMessage failed:', e);
          throw e;
        })
      );
    },

    async deleteDmChatMessage(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('messages:deleteDmChat', {
          charId: args.charId,
          messageId: args.messageId,
        }).catch(e => {
          console.error('ConvexDriver.deleteDmChatMessage failed:', e);
          throw e;
        })
      );
    },

    async upsertJournalEntry(args = {}) {
      const payload = normalizeEntryCommandPayload(args, 'journal entry');
      return await markConnectedOnSuccess(
        client.mutation('journal:upsertEntry', {
          entryId: payload.entryId,
          dataJson: JSON.stringify(payload.entry),
        }).catch(e => {
          console.error('ConvexDriver.upsertJournalEntry failed:', e);
          throw e;
        })
      );
    },

    async deleteJournalEntry(args = {}) {
      const payload = normalizeEntryDeletePayload(args, 'journal entry');
      return await markConnectedOnSuccess(
        client.mutation('journal:deleteEntry', payload).catch(e => {
          console.error('ConvexDriver.deleteJournalEntry failed:', e);
          throw e;
        })
      );
    },

    // Campaign tag (2026-07-13): moves an entry between the universal "Lore"
    // scope (campaignId null) and a campaign scope. Authority is enforced
    // server-side (entry owner or campaign DM).
    async setJournalEntryCampaignScope(args = {}) {
      const entryId = typeof args.entryId === 'string' ? args.entryId.trim() : '';
      if (!entryId) throw new Error('setJournalEntryCampaignScope requires an entryId');
      return await markConnectedOnSuccess(
        client.mutation('journal:setEntryCampaignScope', {
          entryId,
          campaignId: args.campaignId || null,
        }).catch(e => {
          console.error('ConvexDriver.setJournalEntryCampaignScope failed:', e);
          throw e;
        })
      );
    },

    async upsertDmJournalEntry(args = {}) {
      const payload = normalizeEntryCommandPayload(args, 'DM journal entry');
      return await markConnectedOnSuccess(
        client.mutation('dmJournal:upsertEntry', {
          entryId: payload.entryId,
          dataJson: JSON.stringify(payload.entry),
        }).catch(e => {
          console.error('ConvexDriver.upsertDmJournalEntry failed:', e);
          throw e;
        })
      );
    },

    async deleteDmJournalEntry(args = {}) {
      const payload = normalizeEntryDeletePayload(args, 'DM journal entry');
      return await markConnectedOnSuccess(
        client.mutation('dmJournal:deleteEntry', payload).catch(e => {
          console.error('ConvexDriver.deleteDmJournalEntry failed:', e);
          throw e;
        })
      );
    },

    async upsertGlobalLoreEntry(args = {}) {
      const payload = normalizeEntryCommandPayload(args, 'lore entry');
      return await markConnectedOnSuccess(
        client.mutation('lore:upsertGlobalEntry', {
          entryId: payload.entryId,
          dataJson: JSON.stringify(payload.entry),
        }).catch(e => {
          console.error('ConvexDriver.upsertGlobalLoreEntry failed:', e);
          throw e;
        })
      );
    },

    async deleteGlobalLoreEntry(args = {}) {
      const payload = normalizeEntryDeletePayload(args, 'lore entry');
      return await markConnectedOnSuccess(
        client.mutation('lore:deleteGlobalEntry', payload).catch(e => {
          console.error('ConvexDriver.deleteGlobalLoreEntry failed:', e);
          throw e;
        })
      );
    },

    async lookupJournalEntriesByName(args = {}) {
      const payload = normalizeJournalNameLookupPayload(args);
      return await markConnectedOnSuccess(
        client.query('journal:lookupByCategoryNameKeys', payload).catch(e => {
          console.error('ConvexDriver.lookupJournalEntriesByName failed:', e);
          throw e;
        })
      );
    },

    async lookupDmJournalEntriesByName(args = {}) {
      const payload = normalizeJournalNameLookupPayload(args);
      return await markConnectedOnSuccess(
        client.query('dmJournal:lookupByCategoryNameKeys', payload).catch(e => {
          console.error('ConvexDriver.lookupDmJournalEntriesByName failed:', e);
          throw e;
        })
      );
    },

    async discoverJournalEntries(args = {}) {
      const payload = normalizeJournalDiscoveryPayload(args);
      return await markConnectedOnSuccess(
        client.query('journal:discover', payload).catch(e => {
          console.error('ConvexDriver.discoverJournalEntries failed:', e);
          throw e;
        })
      );
    },

    async discoverDmJournalEntries(args = {}) {
      const payload = normalizeJournalDiscoveryPayload(args);
      return await markConnectedOnSuccess(
        client.query('dmJournal:discover', payload).catch(e => {
          console.error('ConvexDriver.discoverDmJournalEntries failed:', e);
          throw e;
        })
      );
    },

    async patchCharacterVitals(args = {}) {
      const payload = {
        charId: args.charId,
        updates: args.updates && typeof args.updates === 'object' ? args.updates : {},
      };
      if (args.expected && typeof args.expected === 'object') {
        payload.expected = args.expected;
      }
      if (typeof args.surface === 'string' && args.surface) {
        payload.surface = args.surface;
      }
      return await markConnectedOnSuccess(
        client.mutation('chars:patchCharacterVitals', payload).catch(e => {
          console.error('ConvexDriver.patchCharacterVitals failed:', e);
          throw e;
        })
      );
    },

    async patchCharacterSystemInstance(args = {}) {
      const payload = {
        charId: args.charId,
        instanceKey: args.instanceKey,
        patch: args.patch && typeof args.patch === 'object' ? args.patch : {},
      };
      if (args.historyEntry && typeof args.historyEntry === 'object') {
        payload.historyEntry = args.historyEntry;
      }
      return await markConnectedOnSuccess(
        client.mutation('chars:patchSystemInstance', payload).catch(e => {
          console.error('ConvexDriver.patchCharacterSystemInstance failed:', e);
          throw e;
        })
      );
    },

    async patchCharacterSystemState(args = {}) {
      const payload = {
        charId: args.charId,
        updates: args.updates && typeof args.updates === 'object' ? args.updates : {},
      };
      if (args.expected && typeof args.expected === 'object') {
        payload.expected = args.expected;
      }
      if (args.audit && typeof args.audit === 'object') {
        payload.audit = args.audit;
      }
      return await markConnectedOnSuccess(
        client.mutation('chars:patchSystemState', payload).catch(e => {
          console.error('ConvexDriver.patchCharacterSystemState failed:', e);
          throw e;
        })
      );
    },

    async patchCharacterSystemStateAndVitals(args = {}) {
      const payload = {
        charId: args.charId,
      };
      if (args.systemUpdates && typeof args.systemUpdates === 'object') {
        payload.systemUpdates = args.systemUpdates;
      }
      if (args.systemExpected && typeof args.systemExpected === 'object') {
        payload.systemExpected = args.systemExpected;
      }
      if (args.vitalsUpdates && typeof args.vitalsUpdates === 'object') {
        payload.vitalsUpdates = args.vitalsUpdates;
      }
      if (args.vitalsExpected && typeof args.vitalsExpected === 'object') {
        payload.vitalsExpected = args.vitalsExpected;
      }
      if (args.audit && typeof args.audit === 'object') {
        payload.audit = args.audit;
      }
      return await markConnectedOnSuccess(
        client.mutation('chars:patchSystemStateAndVitals', payload).catch(e => {
          console.error('ConvexDriver.patchCharacterSystemStateAndVitals failed:', e);
          throw e;
        })
      );
    },

    async patchCharacterSystemStateAndResources(args = {}) {
      const payload = {
        charId: args.charId,
      };
      if (args.systemUpdates && typeof args.systemUpdates === 'object') {
        payload.systemUpdates = args.systemUpdates;
      }
      if (args.systemExpected && typeof args.systemExpected === 'object') {
        payload.systemExpected = args.systemExpected;
      }
      if (args.resourceUpdates && typeof args.resourceUpdates === 'object') {
        payload.resourceUpdates = args.resourceUpdates;
      }
      if (args.resourceExpected && typeof args.resourceExpected === 'object') {
        payload.resourceExpected = args.resourceExpected;
      }
      if (args.audit && typeof args.audit === 'object') {
        payload.audit = args.audit;
      }
      return await markConnectedOnSuccess(
        client.mutation('chars:patchSystemStateAndResources', payload).catch(e => {
          console.error('ConvexDriver.patchCharacterSystemStateAndResources failed:', e);
          throw e;
        })
      );
    },

    async patchCharacterSystemStateAndEffects(args = {}) {
      const payload = {
        charId: args.charId,
      };
      if (args.systemUpdates && typeof args.systemUpdates === 'object') {
        payload.systemUpdates = args.systemUpdates;
      }
      if (args.systemExpected && typeof args.systemExpected === 'object') {
        payload.systemExpected = args.systemExpected;
      }
      if (args.effectUpdates && typeof args.effectUpdates === 'object') {
        payload.effectUpdates = args.effectUpdates;
      }
      if (args.effectExpected && typeof args.effectExpected === 'object') {
        payload.effectExpected = args.effectExpected;
      }
      if (args.audit && typeof args.audit === 'object') {
        payload.audit = args.audit;
      }
      return await markConnectedOnSuccess(
        client.mutation('chars:patchSystemStateAndEffects', payload).catch(e => {
          console.error('ConvexDriver.patchCharacterSystemStateAndEffects failed:', e);
          throw e;
        })
      );
    },

    save(path, value, options = {}) {
      // Session E (2026-04-29): messages per-row diff dispatch. Structurally
      // different from chars/spells/npcs (nested arrays + maps) so handled
      // separately. Same lastDispatched + ref-equality diff strategy.
      if (path === 'messages') {
        const prev = lastDispatched.get('messages') || {};
        const newMap = flattenMessagesBlob(value);
        const isExplicitFullSnapshot = options.fullSnapshot === true;
        const hasTrustedSnapshot = trustedFullSnapshots.has('messages');
        for (const [id, wrapper] of Object.entries(newMap)) {
          const prevWrapper = prev[id];
          // Compare on inner message ref. React's update patterns
          // ([...arr, m] / arr.map(x => x.id === id ? {...x, ...e} : x))
          // preserve message refs for unchanged entries.
          if (!prevWrapper || prevWrapper.message !== wrapper.message) {
            const args = {
              messageId: id,
              channel: wrapper.channel,
              data: JSON.stringify(wrapper.message),
            };
            markConnectedOnSuccess(
              client.mutation('messages:upsertOne', args).catch(e => {
                console.error(`ConvexDriver upsert error on messages:${id}:`, e);
              })
            );
          }
        }
        if (isExplicitFullSnapshot && hasTrustedSnapshot) {
          for (const id of Object.keys(prev)) {
            if (!(id in newMap)) {
              markConnectedOnSuccess(
                client.mutation('messages:deleteOne', { messageId: id }).catch(e => {
                  console.error(`ConvexDriver delete error on messages:${id}:`, e);
                })
              );
            }
          }
          lastDispatched.set('messages', newMap);
        } else {
          if (isExplicitFullSnapshot && !hasTrustedSnapshot) {
            console.warn(
              'ConvexDriver: messages save was marked fullSnapshot but no trusted full snapshot is loaded; dispatching upserts only.'
            );
          }
          lastDispatched.set('messages', { ...prev, ...newMap });
        }
        return;
      }

      if (path === 'journal' || path === 'dmJournal') {
        const config = JOURNAL_ROW_MUTATIONS[path];
        const fallback = SINGLETON_BLOB_DEFAULTS[path];
        const nextValue = Array.isArray(value) ? value : fallback;
        const next = flattenJournalEntries(nextValue);
        const isExplicitFullSnapshot = options.fullSnapshot === true;
        const hasTrustedSnapshot = trustedFullSnapshots.has(path);
        if (next.missingIds > 0 || next.duplicateIds > 0) {
          const message = `ConvexDriver: refusing ${path} save with missing or duplicate journal entry ids.`;
          if (options.allowLegacyJournalSet === true) {
            console.warn(`${message} Falling back to legacy whole-array save because allowLegacyJournalSet is true.`);
          } else {
            console.warn(message);
            return Promise.reject(new Error(message));
          }
        } else {
          const prev = lastDispatched.get(path) || {};
          const mutations = [];
          for (const [entryId, entry] of Object.entries(next.map)) {
            if (entry !== prev[entryId]) {
              mutations.push(
                client.mutation(config.upsertMutation, {
                  entryId,
                  dataJson: JSON.stringify(entry),
                }).catch(e => {
                  console.error(`ConvexDriver journal row upsert error on ${path}:${entryId}:`, e);
                  throw e;
                })
              );
            }
          }
          if (isExplicitFullSnapshot && hasTrustedSnapshot) {
            for (const entryId of Object.keys(prev)) {
              if (!(entryId in next.map)) {
                mutations.push(
                  client.mutation(config.deleteMutation, { entryId }).catch(e => {
                    console.error(`ConvexDriver journal row delete error on ${path}:${entryId}:`, e);
                    throw e;
                  })
                );
              }
            }
            lastDispatched.set(path, next.map);
            trustedFullSnapshots.add(path);
          } else {
            if (isExplicitFullSnapshot && !hasTrustedSnapshot) {
              console.warn(
                `ConvexDriver: ${path} save was marked fullSnapshot but no trusted full snapshot is loaded; dispatching upserts only.`
              );
            }
            lastDispatched.set(path, { ...prev, ...next.map });
          }
          return markConnectedOnSuccess(Promise.all(mutations));
        }
      }

      // Session A (2026-04-28) + Session B (2026-04-29): per-record dispatch.
      // Diffs the saved app-shape map (normalized via extractMap) against
      // lastDispatched and emits one patchOne / deleteOne per change. Cuts
      // chars/spells/npcs upsertAll bandwidth ~95% — instead of rewriting
      // all rows on every save, only the changed record's row is sent.
      if (PER_RECORD_PATHS[path]) {
        const config = PER_RECORD_PATHS[path];
        const prev = lastDispatched.get(path) || {};
        const newMap = config.extractMap(value);
        const mutations = [];

        // Patches: any record in newMap whose value !== prev's value.
        // Reference inequality is the right check here — the app's update
        // patterns ({...prev, [k]: x} for maps, arr.map(x => x.id === id
        // ? {...x, ...e} : x) for arrays) preserve refs for unchanged
        // records, so unchanged rows don't get re-dispatched.
        for (const [key, val] of Object.entries(newMap)) {
          if (val !== prev[key]) {
            const args = { [config.keyField]: key, data: config.encode(val) };
            mutations.push(
              client.mutation(config.patchMutation, args).catch(e => {
                console.error(`ConvexDriver patch error on ${path}:${key}:`, e);
                throw e;
              })
            );
          }
        }
        // Deletes: any record in prev that's missing from newMap.
        if (!config.deleteRequiresFullSnapshot || options.fullSnapshot === true) {
          for (const key of Object.keys(prev)) {
            if (!(key in newMap)) {
              const args = { [config.keyField]: key };
              mutations.push(
                client.mutation(config.deleteMutation, args).catch(e => {
                  console.error(`ConvexDriver delete error on ${path}:${key}:`, e);
                  throw e;
                })
              );
            }
          }
        }
        return markConnectedOnSuccess(Promise.all(mutations).then((result) => {
          const nextDispatched = config.deleteRequiresFullSnapshot && options.fullSnapshot !== true
            ? { ...prev, ...newMap }
            : newMap;
          lastDispatched.set(path, nextDispatched);
          return result;
        }));
      }

      const mutationFn = MUTATION_FUNCTIONS[path];
      if (!mutationFn) {
        console.warn(`ConvexDriver has no mutation function for path "${path}".`);
        return;
      }
      // Three arg shapes:
      //  1. Singleton blob path (Session 5): stringify value, pass as { data }.
      //  2. Custom translator (APP_SHAPE_TO_MUTATION_ARGS): call it.
      //  3. Fallback: wrap as { value } (for settings slice setters).
      let args;
      if (SINGLETON_BLOB_PATHS.has(path)) {
        const fallback = SINGLETON_BLOB_DEFAULTS[path];
        args = { data: JSON.stringify(value ?? fallback) };
        if (path === 'combat') {
          const expectedUpdatedAt = options.expectedUpdatedAt ?? value?._serverUpdatedAt;
          const expectedVersion = options.expectedVersion ?? value?._serverVersion;
          if (expectedUpdatedAt != null) args.expectedUpdatedAt = expectedUpdatedAt;
          if (expectedVersion != null) args.expectedVersion = expectedVersion;
        }
      } else if (APP_SHAPE_TO_MUTATION_ARGS[path]) {
        args = APP_SHAPE_TO_MUTATION_ARGS[path](value);
      } else {
        args = { value };
      }
      return markConnectedOnSuccess(
        client.mutation(mutationFn, args).catch(e => {
          console.error(`ConvexDriver save error on ${path}:`, e);
          throw e;
        })
      );
    },

    async updateMultiple(patch) {
      // Multi-path patches with slash-separated keys (e.g. Firebase-era
      // `updateGameData({'audit/activeSessionId': id, 'audit/snapshots/.../start': v})`).
      //
      // Key concern: callers assume Firebase's multi-path `.update()` semantic —
      // each key DEEPLY patches into the collection at that nested path WITHOUT
      // clobbering sibling fields. Naively calling `save(top, value)` would
      // replace the whole collection with just the sub-path's value, destroying
      // the rest.
      //
      // Approach: bucket keys by top-level path, strip the prefix from each
      // key, and dispatch to the collection's dedicated `applyPatch` mutation
      // (server-side transactional read-modify-write, safe against concurrent
      // append mutations). Currently only `audit:applyPatch` exists; other
      // singleton paths (combat/journal/etc.) don't have nested-patch callers.
      // If one appears, add `applyPatch` to that singleton and extend below.
      if (!patch || typeof patch !== 'object') return;

      /** @type {Map<string, Record<string, any>>} */
      const bucketsByTop = new Map();
      for (const [key, value] of Object.entries(patch)) {
        const parts = key.split('/').filter(Boolean);
        const top = parts[0];
        if (!top) continue;
        if (!bucketsByTop.has(top)) bucketsByTop.set(top, {});
        // Strip the top-level prefix; the remainder is the path relative to
        // the collection root. If key === top (no nested), relative becomes ''.
        const relative = parts.slice(1).join('/');
        bucketsByTop.get(top)[relative] = value;
      }

      for (const [top, subpatches] of bucketsByTop) {
        // Route to the collection's applyPatch mutation if it has one.
        if (top === 'audit') {
          // JSON-encode subpatches (same reason as appendEvent/appendSession —
          // patch values may include char snapshots with emoji-keyed buffSources).
          try {
            await client.mutation('audit:applyPatch', { patches: JSON.stringify(subpatches) });
          } catch (e) {
            console.error('ConvexDriver.updateMultiple: audit:applyPatch failed', e);
          }
          continue;
        }

        // Fallback: if the patch is a whole-collection replace (single key ==
        // top with empty relative), use save(). Otherwise warn — nested patches
        // to non-audit paths aren't supported by this driver yet.
        const keys = Object.keys(subpatches);
        if (keys.length === 1 && keys[0] === '') {
          if (top in MUTATION_FUNCTIONS) this.save(top, subpatches['']);
        } else if (top in MUTATION_FUNCTIONS) {
          console.warn(
            `ConvexDriver.updateMultiple: nested patch for "${top}" isn't supported yet. ` +
            `Keys: ${keys.join(', ')}. Add an applyPatch mutation for "${top}" if needed.`,
            subpatches
          );
        }
      }
    },

    async pushAuditEvent(event) {
      // Session 5: audit is Convex-routed. Optimistic local cache update, then
      // fire `audit:appendEvent` mutation (server-side transactional append).
      // Client generates the id so callers can cache-key on it synchronously,
      // matching the Firebase driver's push-id semantic.
      const id = event?.id || generateLocalId();
      const payload = { ...(event || {}), id };
      const currentAudit = cache.get('audit') || SINGLETON_BLOB_DEFAULTS.audit;
      const nextAudit = {
        ...currentAudit,
        events: { ...(currentAudit.events || {}), [id]: payload },
      };
      cache.set('audit', nextAudit);
      notify('audit');
      // JSON-encode the payload — audit event bodies embed objects keyed by
      // buff/debuff display names (which include emoji prefixes like
      // "⚠️ Observed"). Convex's args validator rejects non-ASCII keys in
      // v.any() fields; stringifying treats the whole payload as opaque.
      // Server-side appendEvent parses and appends. See convex/audit.ts.
      try {
        await client.mutation('audit:appendEvent', { event: JSON.stringify(payload) });
      } catch (e) {
        console.error('ConvexDriver.pushAuditEvent failed:', e);
      }
      return id;
    },

    async pushAuditSession(session) {
      const id = session?.id || generateLocalId();
      const payload = { ...(session || {}), id };
      const currentAudit = cache.get('audit') || SINGLETON_BLOB_DEFAULTS.audit;
      const nextAudit = {
        ...currentAudit,
        sessions: { ...(currentAudit.sessions || {}), [id]: payload },
      };
      cache.set('audit', nextAudit);
      notify('audit');
      try {
        await client.mutation('audit:appendSession', { session: JSON.stringify(payload) });
      } catch (e) {
        console.error('ConvexDriver.pushAuditSession failed:', e);
      }
      return id;
    },

    async depositPartyInventoryFromChar(args) {
      return await markConnectedOnSuccess(
        client.mutation('partyInventory:depositFromChar', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          quantity: args.quantity,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.depositPartyInventoryFromChar failed:', e);
          throw e;
        })
      );
    },

    async takePartyInventoryToChar(args) {
      return await markConnectedOnSuccess(
        client.mutation('partyInventory:takeToChar', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          quantity: args.quantity,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.takePartyInventoryToChar failed:', e);
          throw e;
        })
      );
    },

    async transferInventoryItemBetweenChars(args) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:transferInventoryItemBetweenChars', {
          sourceCharId: args.sourceCharId,
          targetCharId: args.targetCharId,
          itemEntryId: args.itemEntryId,
          quantity: args.quantity,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.transferInventoryItemBetweenChars failed:', e);
          throw e;
        })
      );
    },

    async moveCharItemToCraftingTable(args) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:moveCharItemToCraftingTable', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          quantity: args.quantity,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.moveCharItemToCraftingTable failed:', e);
          throw e;
        })
      );
    },

    async addInventoryItem(args = {}) {
      const idempotencyKey = args.idempotencyKey || args.item?.id || generateLocalId();
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:addInventoryItem', {
          charId: args.charId,
          itemJson: JSON.stringify(args.item || {}),
          idempotencyKey,
        }).catch(e => {
          console.error('ConvexDriver.addInventoryItem failed:', e);
          throw e;
        })
      );
    },

    async updateInventoryItem(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:updateInventoryItem', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          updatesJson: JSON.stringify(args.updates || {}),
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.updateInventoryItem failed:', e);
          throw e;
        })
      );
    },

    async removeInventoryItem(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:removeInventoryItem', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.removeInventoryItem failed:', e);
          throw e;
        })
      );
    },

    async setInventoryItemFavorite(args = {}) {
      if (typeof args.favorite !== 'boolean') {
        throw new Error('setInventoryItemFavorite requires favorite target state');
      }
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:setInventoryItemFavorite', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          favorite: args.favorite,
          expectedFavorited: args.expectedFavorited ?? null,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.setInventoryItemFavorite failed:', e);
          throw e;
        })
      );
    },

    async toggleInventoryItemEquipped(args = {}) {
      if (typeof args.expectedEquipped !== 'boolean') {
        throw new Error('toggleInventoryItemEquipped requires expectedEquipped target state');
      }
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:toggleInventoryItemEquipped', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          expectedEquipped: args.expectedEquipped,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.toggleInventoryItemEquipped failed:', e);
          throw e;
        })
      );
    },

    async consumeInventoryItem(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:consumeInventoryItem', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          expectedQuantity: args.expectedQuantity,
          expectedUsesCurrent: args.expectedUsesCurrent ?? null,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.consumeInventoryItem failed:', e);
          throw e;
        })
      );
    },

    async activateInventoryItemAbility(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:activateInventoryItemAbility', {
          charId: args.charId,
          itemEntryId: args.itemEntryId,
          itemDefinitionId: args.itemDefinitionId ?? null,
          abilityId: args.abilityId,
          expectedUsesCurrent: args.expectedUsesCurrent ?? null,
          expectedUsesMax: args.expectedUsesMax ?? null,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.activateInventoryItemAbility failed:', e);
          throw e;
        })
      );
    },

    async craftOutputToChar(args) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:craftOutputToChar', {
          charId: args.charId,
          recipeResultJson: JSON.stringify(args.recipeResult || {}),
          quantity: Math.max(1, Number(args.quantity) || 1),
          consumedIngredients: Array.isArray(args.consumedIngredients) ? args.consumedIngredients : [],
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.craftOutputToChar failed:', e);
          throw e;
        })
      );
    },

    async craftOutputToParty(args) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:craftOutputToParty', {
          charId: args.charId,
          recipeResultJson: JSON.stringify(args.recipeResult || {}),
          quantity: Math.max(1, Number(args.quantity) || 1),
          consumedIngredients: Array.isArray(args.consumedIngredients) ? args.consumedIngredients : [],
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.craftOutputToParty failed:', e);
          throw e;
        })
      );
    },

    async tableRowToChar(args) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:tableRowToChar', {
          charId: args.charId,
          tableItemId: args.tableItemId,
          quantity: args.quantity,
          fallbackResultJson: args.fallbackResult ? JSON.stringify(args.fallbackResult) : undefined,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.tableRowToChar failed:', e);
          throw e;
        })
      );
    },

    async tableRowToParty(args) {
      return await markConnectedOnSuccess(
        client.mutation('itemTransfers:tableRowToParty', {
          charId: args.charId,
          tableItemId: args.tableItemId,
          quantity: args.quantity,
          fallbackResultJson: args.fallbackResult ? JSON.stringify(args.fallbackResult) : undefined,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.tableRowToParty failed:', e);
          throw e;
        })
      );
    },

    async activateCombatItemAbility(args = {}) {
      const payload = {
        charId: args.charId,
        combatId: args.combatId ?? null,
        itemEntryId: args.itemEntryId,
        abilityId: args.abilityId,
        targetId: args.targetId ?? null,
        expectedTargetType: args.expectedTargetType ?? null,
        isCrit: args.isCrit === true,
        expectedAbilityUsesCurrent: args.expectedAbilityUsesCurrent ?? null,
        expectedAbilityUsesMax: args.expectedAbilityUsesMax ?? null,
        expectedCombatRound: args.expectedCombatRound ?? null,
        expectedCombatTurnIndex: args.expectedCombatTurnIndex ?? null,
        expectedTargetCurrentHp: args.expectedTargetCurrentHp ?? null,
        expectedTargetTempHp: args.expectedTargetTempHp ?? null,
        nonlethalOnly: args.nonlethalOnly === true,
        saveAbility: args.saveAbility ?? null,
        saveDC: args.saveDC ?? null,
        saveEffect: args.saveEffect ?? null,
        saveTotal: args.saveTotal ?? null,
        savePassed: typeof args.savePassed === 'boolean' ? args.savePassed : null,
        activationSurface: args.activationSurface || 'combat',
        idempotencyKey: args.idempotencyKey || generateLocalId(),
      };
      if (args.itemName) payload.itemName = args.itemName;
      if (args.abilityName) payload.abilityName = args.abilityName;
      return await markConnectedOnSuccess(
        client.mutation('itemAbilities:activateCombatItemAbility', payload).catch(e => {
          console.error('ConvexDriver.activateCombatItemAbility failed:', e);
          throw e;
        })
      );
    },

    async applyEffectInstanceLifecycle(args = {}) {
      const payload = {
        effectInstanceId: args.effectInstanceId,
        action: args.action,
        expectedLegacyKey: args.expectedLegacyKey ?? null,
        expectedStatus: args.expectedStatus ?? null,
      };
      return await markConnectedOnSuccess(
        client.mutation('effectInstances:applyLifecycle', payload).catch(e => {
          console.error('ConvexDriver.applyEffectInstanceLifecycle failed:', e);
          throw e;
        })
      );
    },

    async reconcileItemAbilityEffectInstancesForChar(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('effectInstances:reconcileItemAbilityForChar', {
          charId: args.charId,
          dryRun: args.dryRun === true,
          confirm: args.confirm,
        }).catch(e => {
          console.error('ConvexDriver.reconcileItemAbilityEffectInstancesForChar failed:', e);
          throw e;
        })
      );
    },

    async purchaseShopListing(args) {
      return await markConnectedOnSuccess(
        client.mutation('shopPurchases:purchaseShopListing', {
          charId: args.charId,
          shopId: args.shopId,
          listingId: args.listingId,
          quantity: Math.max(1, Number(args.quantity) || 1),
          expectedPrice: args.expectedPrice,
          expectedListingVersion: args.expectedListingVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.purchaseShopListing failed:', e);
          throw e;
        })
      );
    },

    async checkoutShopCart(args) {
      return await markConnectedOnSuccess(
        client.mutation('shopPurchases:checkoutShopCart', {
          charId: args.charId,
          shopId: args.shopId,
          lines: Array.isArray(args.lines) ? args.lines.map((line) => ({
            listingId: line.listingId,
            quantity: Math.max(1, Number(line.quantity) || 1),
            expectedPrice: line.expectedPrice,
            expectedListingVersion: line.expectedListingVersion,
          })) : [],
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.checkoutShopCart failed:', e);
          throw e;
        })
      );
    },

    async sellInventoryItemToShopForDm(args) {
      return await markConnectedOnSuccess(
        client.mutation('shopTrades:sellInventoryItemToShopForDm', {
          charId: args.charId,
          shopId: args.shopId,
          itemEntryId: args.itemEntryId,
          quantity: Math.max(1, Number(args.quantity) || 1),
          payoutBase: Math.max(0, Math.trunc(Number(args.payoutBase) || 0)),
          expectedShopVersion: args.expectedShopVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.sellInventoryItemToShopForDm failed:', e);
          throw e;
        })
      );
    },

    async submitTradeOffer(args) {
      return await markConnectedOnSuccess(
        client.mutation('shopTrades:submitTradeOffer', {
          charId: args.charId,
          shopId: args.shopId,
          offeredItems: Array.isArray(args.offeredItems) ? args.offeredItems.map((line) => ({
            itemEntryId: line.itemEntryId,
            quantity: Math.max(1, Number(line.quantity) || 1),
          })) : [],
          offeredGoldBase: args.offeredGoldBase,
          requestedListings: Array.isArray(args.requestedListings) ? args.requestedListings.map((line) => ({
            listingId: line.listingId,
            quantity: Math.max(1, Number(line.quantity) || 1),
            expectedListingVersion: line.expectedListingVersion,
            expectedPrice: line.expectedPrice,
          })) : [],
          requestedGoldBase: args.requestedGoldBase,
          note: args.note,
          expectedShopVersion: args.expectedShopVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.submitTradeOffer failed:', e);
          throw e;
        })
      );
    },

    async cancelTradeOffer(args) {
      return await markConnectedOnSuccess(
        client.mutation('shopTrades:cancelTradeOffer', {
          offerId: args.offerId,
          expectedOfferVersion: args.expectedOfferVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
          reason: args.reason,
        }).catch(e => {
          console.error('ConvexDriver.cancelTradeOffer failed:', e);
          throw e;
        })
      );
    },

    async denyTradeOfferForDm(args) {
      return await markConnectedOnSuccess(
        client.mutation('shopTrades:denyTradeOfferForDm', {
          offerId: args.offerId,
          expectedOfferVersion: args.expectedOfferVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
          reason: args.reason,
        }).catch(e => {
          console.error('ConvexDriver.denyTradeOfferForDm failed:', e);
          throw e;
        })
      );
    },

    async settleTradeOfferForDm(args) {
      return await markConnectedOnSuccess(
        client.mutation('shopTrades:settleTradeOfferForDm', {
          offerId: args.offerId,
          expectedOfferVersion: args.expectedOfferVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.settleTradeOfferForDm failed:', e);
          throw e;
        })
      );
    },

    async promoteTradeReceiptLineToShopListingForDm(args) {
      return await markConnectedOnSuccess(
        client.mutation('shopTrades:promoteTradeReceiptLineToShopListingForDm', {
          transactionId: args.transactionId,
          lineIndex: args.lineIndex,
          priceBase: args.priceBase,
          secrecy: args.secrecy,
          tradeOnly: args.tradeOnly,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.promoteTradeReceiptLineToShopListingForDm failed:', e);
          throw e;
        })
      );
    },

    async adjustShopWalletForDm(args) {
      return await markConnectedOnSuccess(
        client.mutation('shops:adjustShopWalletForAuthedDm', {
          shopId: args.shopId,
          deltaBase: Math.trunc(Number(args.deltaBase) || 0),
          reason: args.reason,
          expectedWalletVersion: args.expectedWalletVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.adjustShopWalletForDm failed:', e);
          throw e;
        })
      );
    },

    async importShopDataForDm(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('shops:importShopDataForAuthedDm', {
          shopDataJson: JSON.stringify(args.shopData || {}),
          dryRun: args.dryRun === true,
          confirm: args.confirm,
        }).catch(e => {
          console.error('ConvexDriver.importShopDataForDm failed:', e);
          throw e;
        })
      );
    },

    async createDmShop(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('shops:createShopForAuthedDm', {
          clientShopId: args.clientShopId,
          activate: args.activate === true,
          patch: args.patch || {},
        }).catch(e => {
          console.error('ConvexDriver.createDmShop failed:', e);
          throw e;
        })
      );
    },

    async deleteDmShop(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('shops:deleteShopForAuthedDm', {
          shopId: args.shopId,
          expectedVersion: args.expectedVersion,
          replacementActiveShopId: args.replacementActiveShopId,
        }).catch(e => {
          console.error('ConvexDriver.deleteDmShop failed:', e);
          throw e;
        })
      );
    },

    async updateDmShop(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('shops:updateShopForAuthedDm', {
          shopId: args.shopId,
          expectedVersion: args.expectedVersion,
          patch: args.patch || {},
        }).catch(e => {
          console.error('ConvexDriver.updateDmShop failed:', e);
          throw e;
        })
      );
    },

    async createDmShopListing(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('shops:createShopListingForAuthedDm', {
          shopId: args.shopId,
          clientListingId: args.clientListingId,
          definitionId: args.definitionId,
          templateSnapshotJson: args.templateSnapshot === undefined ? undefined : JSON.stringify(args.templateSnapshot || {}),
          patch: args.patch || {},
        }).catch(e => {
          console.error('ConvexDriver.createDmShopListing failed:', e);
          throw e;
        })
      );
    },

    async deleteDmShopListing(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('shops:deleteShopListingForAuthedDm', {
          listingId: args.listingId,
          expectedVersion: args.expectedVersion,
        }).catch(e => {
          console.error('ConvexDriver.deleteDmShopListing failed:', e);
          throw e;
        })
      );
    },

    async updateDmShopListing(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('shops:updateShopListingForAuthedDm', {
          listingId: args.listingId,
          expectedVersion: args.expectedVersion,
          patch: args.patch || {},
          templateSnapshotJson: args.templateSnapshot === undefined ? undefined : JSON.stringify(args.templateSnapshot || {}),
        }).catch(e => {
          console.error('ConvexDriver.updateDmShopListing failed:', e);
          throw e;
        })
      );
    },

    async updateDmShopRuntimeState(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('shops:updateShopRuntimeStateForAuthedDm', {
          expectedUpdatedAt: args.expectedUpdatedAt,
          patchJson: JSON.stringify(args.patch || {}),
        }).catch(e => {
          console.error('ConvexDriver.updateDmShopRuntimeState failed:', e);
          throw e;
        })
      );
    },

    async publishLootEncounterForDm(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:publishLootEncounterForDm', {
          encounterId: args.encounterId,
          enemyId: args.enemyId ?? undefined,
          enemyName: args.enemyName,
          xpTotal: args.xpTotal,
          drops: Array.isArray(args.drops) ? args.drops.map((drop) => ({
            dropId: drop.dropId,
            definitionId: drop.definitionId,
            quantity: Math.max(1, Number(drop.quantity) || 1),
            visibleToPlayers: drop.visibleToPlayers === true,
            snapshotJson: JSON.stringify(drop.snapshot || {}),
            lootTableRowIndex: drop.lootTableRowIndex,
          })) : [],
          expectedEncounterVersion: args.expectedEncounterVersion,
        }).catch(e => {
          console.error('ConvexDriver.publishLootEncounterForDm failed:', e);
          throw e;
        })
      );
    },

    async importLootDataForDm(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:importLootDataForAuthedDm', {
          lootDataJson: JSON.stringify(args.lootData || {}),
          dryRun: args.dryRun === true,
          confirm: args.confirm,
        }).catch(e => {
          console.error('ConvexDriver.importLootDataForDm failed:', e);
          throw e;
        })
      );
    },

    async claimLootDrop(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:claimLootDrop', {
          encounterId: args.encounterId,
          dropId: args.dropId,
          expectedDropVersion: args.expectedDropVersion,
        }).catch(e => {
          console.error('ConvexDriver.claimLootDrop failed:', e);
          throw e;
        })
      );
    },

    async withdrawLootClaim(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:withdrawLootClaim', {
          encounterId: args.encounterId,
          dropId: args.dropId,
          expectedDropVersion: args.expectedDropVersion,
        }).catch(e => {
          console.error('ConvexDriver.withdrawLootClaim failed:', e);
          throw e;
        })
      );
    },

    async setLootDropVisibilityForDm(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:setLootDropVisibilityForDm', {
          encounterId: args.encounterId,
          dropId: args.dropId,
          visible: args.visible === true,
          expectedDropVersion: args.expectedDropVersion,
        }).catch(e => {
          console.error('ConvexDriver.setLootDropVisibilityForDm failed:', e);
          throw e;
        })
      );
    },

    async revealAllLootDropsForDm(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:revealAllLootDropsForDm', {
          encounterId: args.encounterId,
          expectedEncounterVersion: args.expectedEncounterVersion,
        }).catch(e => {
          console.error('ConvexDriver.revealAllLootDropsForDm failed:', e);
          throw e;
        })
      );
    },

    async finishLootEncounterForDm(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:finishLootEncounterForDm', {
          encounterId: args.encounterId,
          expectedEncounterVersion: args.expectedEncounterVersion,
        }).catch(e => {
          console.error('ConvexDriver.finishLootEncounterForDm failed:', e);
          throw e;
        })
      );
    },

    async distributeLootXpForDm(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:distributeLootXpForDm', {
          encounterId: args.encounterId,
          expectedEncounterVersion: args.expectedEncounterVersion,
        }).catch(e => {
          console.error('ConvexDriver.distributeLootXpForDm failed:', e);
          throw e;
        })
      );
    },

    async awardLootDrop(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:awardLootDrop', {
          encounterId: args.encounterId,
          dropId: args.dropId,
          targetCharId: args.targetCharId,
          expectedDropVersion: args.expectedDropVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.awardLootDrop failed:', e);
          throw e;
        })
      );
    },

    async awardCurrencyLoot(args = {}) {
      return await markConnectedOnSuccess(
        client.mutation('lootRuntime:awardCurrencyLoot', {
          encounterId: args.encounterId,
          dropId: args.dropId,
          targetCharId: args.targetCharId,
          expectedDropVersion: args.expectedDropVersion,
          idempotencyKey: args.idempotencyKey || generateLocalId(),
        }).catch(e => {
          console.error('ConvexDriver.awardCurrencyLoot failed:', e);
          throw e;
        })
      );
    },

    flushPending() {
      // Convex mutations are already networked immediately; nothing to flush.
    },

    dispose() {
      subscriptions.forEach(u => { try { u(); } catch (e) { /* noop */ } });
      subscriptions.clear();
      handlers.clear();
      cache.clear();
      try { client.close(); } catch (e) { /* noop */ }
    },

    getConnectionStatus() {
      return connectionStatus;
    },
  };
};
